import { useState, useEffect, useCallback } from 'react'
import { Rss, Plus, Trash2, RefreshCw, Search, CheckCircle, XCircle, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

export default function FeedManagerPage() {
  const [feeds, setFeeds] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [newUrl, setNewUrl] = useState('')
  const [newName, setNewName] = useState('')
  const [testResult, setTestResult] = useState<any>(null)
  const [testing, setTesting] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/feeds/all')
      setFeeds(data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const testFeed = async () => {
    if (!newUrl) return
    setTesting(true)
    setTestResult(null)
    try {
      const { data } = await api.get('/api/feeds/test', { params: { url: newUrl } })
      setTestResult(data)
      if (data.status === 'ok' && !newName) setNewName(data.feed_title || '')
    } finally { setTesting(false) }
  }

  const addFeed = async () => {
    if (!newUrl || !newName) return
    await api.post('/api/feeds/custom', { url: newUrl, name: newName })
    setNewUrl('')
    setNewName('')
    setTestResult(null)
    setShowAdd(false)
    fetchData()
  }

  const removeFeed = async (url: string) => {
    await api.delete('/api/feeds/custom', { params: { url } })
    fetchData()
  }

  const categories = feeds?.categories || {}
  const allFeeds = Object.entries(categories).flatMap(([cat, list]) =>
    (list as any[]).map(f => ({ ...f, category: cat }))
  )
  const filtered = allFeeds.filter(f =>
    !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase()) || f.url.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Rss size={20} className="text-amber" /> Feed Manager
          </h1>
          <p className="text-[10px] text-text-secondary">
            {feeds?.total || 0} feeds ({feeds?.built_in || 0} built-in, {feeds?.custom || 0} custom)
            {' · '}
            <a href="/app/admin/feeds" className="text-blue hover:underline">
              Try the new unified admin page →
            </a>
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowAdd(!showAdd)} className="btn-primary text-xs">
            <Plus size={12} /> Add Feed
          </button>
          <button type="button" onClick={fetchData} className="btn-secondary text-xs">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Add feed form */}
      {showAdd && (
        <div className="card p-4 border border-blue/20">
          <h2 className="text-sm font-semibold text-text-primary mb-3">Add Custom RSS Feed</h2>
          <div className="flex gap-2 mb-2">
            <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://example.com/feed.xml" className="input py-2 text-xs flex-1" />
            <button type="button" onClick={testFeed} disabled={testing || !newUrl} className="btn-secondary text-xs">
              {testing ? <RefreshCw size={12} className="animate-spin" /> : 'Test'}
            </button>
          </div>
          {testResult && (
            <div className={clsx('p-2 rounded text-xs mb-2', testResult.status === 'ok' ? 'bg-emerald/10 text-emerald' : 'bg-rose/10 text-rose-light')}>
              {testResult.status === 'ok' ? (
                <><CheckCircle size={12} className="inline mr-1" /> {testResult.feed_title} — {testResult.total_entries} entries</>
              ) : (
                <><XCircle size={12} className="inline mr-1" /> {testResult.error}</>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Feed name" className="input py-2 text-xs flex-1" />
            <button type="button" onClick={addFeed} disabled={!newUrl || !newName} className="btn-primary text-xs">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Filter feeds..." className="input pl-7 py-1.5 text-[11px]" />
      </div>

      {/* Feed list by category */}
      {Object.entries(categories).map(([cat, list]) => {
        const catFeeds = (list as any[]).filter(f =>
          !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        if (catFeeds.length === 0) return null
        return (
          <div key={cat} className="card p-3">
            <h3 className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">
              {cat.replace(/_/g, ' ')} ({catFeeds.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
              {catFeeds.map((f: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-surface-hover text-[10px]">
                  <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', f.type === 'custom' ? 'bg-amber' : 'bg-emerald')} />
                  <span className="text-text-primary truncate flex-1">{f.name}</span>
                  {f.type === 'custom' && (
                    <button type="button" onClick={() => removeFeed(f.url)} className="text-rose-light hover:text-rose shrink-0" title="Remove">
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
